import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Camera,
  Image as ImageIcon,
  Upload,
  Loader2,
  Trash2,
  Eye,
  X,
  CheckCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

// Photo thumbnail component - fetches blob via API
function PhotoThumb({ fileId, alt, onClick }) {
  const { api } = useAuth();
  const [src, setSrc] = useState(null);

  useEffect(() => {
    let url;
    api.get(`/files/${fileId}/download`, { responseType: 'blob' })
      .then(res => {
        url = URL.createObjectURL(res.data);
        setSrc(url);
      })
      .catch(() => setSrc(null));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [fileId, api]);

  return (
    <button
      onClick={onClick}
      className="relative w-full aspect-square rounded-lg overflow-hidden bg-muted border border-border hover:border-accent transition-colors"
      data-testid={`photo-thumb-${fileId}`}
    >
      {src ? (
        <img src={src} alt={alt} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <ImageIcon className="h-6 w-6 text-muted-foreground/40" />
        </div>
      )}
    </button>
  );
}

// Full-screen photo viewer
function PhotoViewer({ fileId, caption, open, onClose }) {
  const { api } = useAuth();
  const [src, setSrc] = useState(null);

  useEffect(() => {
    if (!open || !fileId) return;
    let url;
    api.get(`/files/${fileId}/download`, { responseType: 'blob' })
      .then(res => {
        url = URL.createObjectURL(res.data);
        setSrc(url);
      });
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [fileId, open, api]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={onClose}>
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-4 right-4 text-white hover:bg-white/20 z-10"
        onClick={onClose}
        data-testid="photo-viewer-close"
      >
        <X className="h-6 w-6" />
      </Button>
      {src ? (
        <img
          src={src}
          alt={caption || 'Photo'}
          className="max-h-[90vh] max-w-[95vw] object-contain"
          onClick={e => e.stopPropagation()}
        />
      ) : (
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      )}
      {caption && (
        <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white bg-black/50 px-4 py-2 rounded-lg text-sm">
          {caption}
        </p>
      )}
    </div>
  );
}

// Photo upload button - opens camera or file picker
function PhotoUploadButton({ orderId, kind, onUploaded, label }) {
  const { api } = useAuth();
  const { t } = useLanguage();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error(t('photos.onlyImages')); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error(t('photos.maxSize')); return; }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post(`/orders/${orderId}/photos/${kind}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(t('photos.uploaded'));
      onUploaded?.();
    } catch (err) {
      toast.error(t('photos.uploadError'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleUpload}
        data-testid={`photo-input-${kind}`}
      />
      <Button
        variant="outline"
        className="h-20 flex-col gap-1 border-dashed"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        data-testid={`photo-upload-${kind}`}
      >
        {uploading ? (
          <Loader2 className="h-6 w-6 animate-spin" />
        ) : (
          <Camera className="h-6 w-6 text-muted-foreground" />
        )}
        <span className="text-xs text-muted-foreground">{label}</span>
      </Button>
    </>
  );
}

// Order Photos Gallery component
export function OrderPhotosGallery({ orderId }) {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewerPhoto, setViewerPhoto] = useState(null);

  const fetchPhotos = useCallback(async () => {
    try {
      const res = await api.get(`/orders/${orderId}/photos`);
      setPhotos(res.data);
    } catch (err) {
      console.error('Failed to fetch photos:', err);
    } finally {
      setLoading(false);
    }
  }, [api, orderId]);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  const beforePhotos = photos.filter(p => p.kind === 'before');
  const afterPhotos = photos.filter(p => p.kind === 'after');
  const otherPhotos = photos.filter(p => p.kind === 'other');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="order-photos-gallery">
      {/* Upload buttons */}
      <div className="grid grid-cols-2 gap-2">
        <PhotoUploadButton
          orderId={orderId}
          kind="before"
          onUploaded={fetchPhotos}
          label={t('photos.before')}
        />
        <PhotoUploadButton
          orderId={orderId}
          kind="after"
          onUploaded={fetchPhotos}
          label={t('photos.after')}
        />
      </div>

      {/* Before photos */}
      {beforePhotos.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2">{t('photos.beforeLabel', beforePhotos.length)}</p>
          <div className="grid grid-cols-3 gap-2">
            {beforePhotos.map(p => (
              <PhotoThumb
                key={p.id}
                fileId={p.file_id}
                alt={p.caption || 'Before'}
                onClick={() => setViewerPhoto(p)}
              />
            ))}
          </div>
        </div>
      )}

      {/* After photos */}
      {afterPhotos.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2">{t('photos.afterLabel', afterPhotos.length)}</p>
          <div className="grid grid-cols-3 gap-2">
            {afterPhotos.map(p => (
              <PhotoThumb
                key={p.id}
                fileId={p.file_id}
                alt={p.caption || 'After'}
                onClick={() => setViewerPhoto(p)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Other */}
      {otherPhotos.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2">{t('photos.otherLabel', otherPhotos.length)}</p>
          <div className="grid grid-cols-3 gap-2">
            {otherPhotos.map(p => (
              <PhotoThumb
                key={p.id}
                fileId={p.file_id}
                alt={p.caption || 'Photo'}
                onClick={() => setViewerPhoto(p)}
              />
            ))}
          </div>
        </div>
      )}

      {photos.length === 0 && (
        <p className="text-xs text-center text-muted-foreground py-3">
          {t('photos.noPhotos')}
        </p>
      )}

      {/* Full screen viewer */}
      <PhotoViewer
        fileId={viewerPhoto?.file_id}
        caption={viewerPhoto?.caption}
        open={!!viewerPhoto}
        onClose={() => setViewerPhoto(null)}
      />
    </div>
  );
}

// File upload component for generic file attachments
export function FileUploadArea({ ownerType, ownerId, onUploaded }) {
  const { api } = useAuth();
  const { t } = useLanguage();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error(t('photos.maxSize')); return; }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post(`/files/upload?owner_type=${ownerType}&owner_id=${ownerId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(t('files.uploaded'));
      onUploaded?.();
    } catch (err) {
      toast.error(t('files.uploadError'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <>
      <input ref={fileRef} type="file" className="hidden" onChange={handleUpload} data-testid="file-upload-input" />
      <Button
        variant="outline"
        className="w-full h-14 border-dashed"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        data-testid="file-upload-btn"
      >
        {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
        {t('files.attach')}
      </Button>
    </>
  );
}

// Files list component
export function FilesList({ ownerType, ownerId }) {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFiles = useCallback(async () => {
    try {
      const endpoint = ownerType === 'order' ? `/orders/${ownerId}/files` : `/files?owner_type=${ownerType}&owner_id=${ownerId}`;
      const res = await api.get(endpoint);
      setFiles(res.data);
    } catch (err) {
      console.error('Failed to fetch files:', err);
    } finally {
      setLoading(false);
    }
  }, [api, ownerType, ownerId]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const handleDelete = async (fileId) => {
    try {
      await api.delete(`/files/${fileId}`);
      toast.success(t('files.deleted'));
      fetchFiles();
    } catch (err) {
      toast.error(t('files.deleteError'));
    }
  };

  const handleDownload = async (file) => {
    try {
      const res = await api.get(`/files/${file.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(t('files.downloadError'));
    }
  };

  if (loading) return <Loader2 className="h-4 w-4 animate-spin mx-auto" />;

  return (
    <div className="space-y-2" data-testid="files-list">
      <FileUploadArea ownerType={ownerType} ownerId={ownerId} onUploaded={fetchFiles} />
      {files.map(f => (
        <div key={f.id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border">
          <div className="w-8 h-8 rounded bg-accent/10 flex items-center justify-center shrink-0">
            {f.content_type?.startsWith('image/') ? (
              <ImageIcon className="h-4 w-4 text-accent" />
            ) : (
              <Upload className="h-4 w-4 text-accent" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{f.filename}</p>
            <p className="text-xs text-muted-foreground">
              {(f.size / 1024).toFixed(0)} KB
            </p>
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDownload(f)}>
              <Eye className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(f.id)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
